import type { HTMLComponent } from "@commercekit/react";
import { useRef, useState } from "react";
import type { UIEvent } from "react";
import WvLink from "~/components/atoms/WvLink";
import { useLocale, useLocalePath } from "@commercekit/remix";
import {
  ProductStockAvailabilityEnum,
  type WovarCustomer,
  type WovarProduct,
} from "@commercekit/api";
import { usePrismicLabels } from "~/hooks/usePrismicLabel";
import {
  fallbackMoney,
  formatMoneyWithCurrency,
  taxExemptedCustomer,
} from "@commercekit/shared";
import { useWvSelectItem } from "~/hooks/useDataLayer";
import { optimizeImageUrl } from "~/lib/imageUtils";
import WvCategoryActionBar from "../molecules/WvCategoryActionBar";
import { getDeliveryEstimate } from "~/lib/deliveryEstimate";

const WvProductPopularAlternatives: HTMLComponent<{
  products: WovarProduct[];
  itemListId: string;
  itemListName: string;
  customer: WovarCustomer;
}> = ({ products, itemListId, itemListName, customer }) => {
  const { localeFullName } = useLocale();
  const { $pt } = usePrismicLabels();
  const localePath = useLocalePath();
  const gtmSelectItem = useWvSelectItem();

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(products?.length > 2);

  const containerRef = useRef<HTMLDivElement>(null);

  const scroll = (direction: "LEFT" | "RIGHT") => {
    const left = direction === "RIGHT" ? 360 : -360;
    containerRef.current?.scrollBy({ behavior: "smooth", left });
  };

  const handleScroll = (e: UIEvent) => {
    const left_offset = e.currentTarget.scrollLeft + 1;
    const container_width = e.currentTarget.scrollWidth;
    const visible_width = e.currentTarget.clientWidth;

    const right_offset = container_width - (visible_width + left_offset);

    setCanScrollLeft(left_offset > 1);
    setCanScrollRight(right_offset > 1);
  };

  const popularAlternativesTitle = `${$pt(
    "product_popular_alternatives_title"
  )}`;

  return (
    <div className="mb-5 component-products">
      <div className="container">
        <h2 className="pb-3 mb-4 text-center heading xl underlined">
          {popularAlternativesTitle}
        </h2>
        <div className="items-center text-center component-product__slider d-flex">
          <div
            className={`component-products-prev d-none d-lg-block top-unset js-scroller-prev ${!canScrollLeft ? "disabled" : ""
              }`}
            data-icon-before=""
            onClick={() => scroll("LEFT")}
          ></div>
          <div className="w-full row-scroller">
            <div
              className="pt-2 pl-2 row justify-content-start no-gutters pl-md-0"
              ref={containerRef}
              onScroll={handleScroll}
            >
              {products?.map((product, index) => (
                <div
                  key={`alternative_product_${index}`}
                  className="px-2 mb-4 col-11 col-sm-8 col-md-6 col-xl-4 component-productblock"
                >
                  <div className="text-left productblock h-100">
                    <div
                      className="productblock__toplabel d-none"
                      data-icon-before=""
                    >
                      Temporarily reduced in price!
                    </div>
                    <WvLink
                      to={localePath(`/${product?.slug}`)}
                      onClick={() =>
                        gtmSelectItem(product, itemListId, itemListName)
                      }
                      className="pt-4 pb-2 d-flex flex-column justify-content-between h-100"
                    >
                      <div className="row">
                        <div className="col-12 col-md-8 order-md-1">
                          <span className="productblock__title heading sans-serif strong d-block">
                            {product?.name}
                          </span>
                          <span
                            className="mt-2 mb-3 productblock__stock-delivery d-block mb-md-2 mt-md-1"
                            {...(product?.unitsOfMeasure[0]?.availability
                              .stockAvailabilityMessage !==
                              ProductStockAvailabilityEnum.IMPOSSIBLE_TO_ORDER &&
                              product.stockLevel > 0
                              ? { "data-icon-before": "" }
                              : {})}
                          >
                            {getDeliveryEstimate({
                              $pt,
                              deliveryEstimate:
                                product?.unitsOfMeasure[0]?.availability
                                  ?.deliveryEstimateMessage,
                              deliveryDate:
                                product?.unitsOfMeasure[0]?.availability
                                  ?.deliveryDate,
                              workingDays:
                                product?.unitsOfMeasure[0]?.availability
                                  ?.workingDays,
                              defaultShipping:
                                product?.unitsOfMeasure[0].defaultShipping,
                            })}
                          </span>
                          <span className="d-none d-md-block">
                            {product?.productUnit && (
                              <span className="productblock__label d-inline-block">
                                {product?.productUnit}
                              </span>
                            )}
                          </span>
                        </div>
                        <div className="col-12 col-md-4">
                          <span className="row no-gutters">
                            <span className="col-6 col-md-12 productblock__image img">
                              <img
                                src={optimizeImageUrl(
                                  product?.defaultImage?.urlThumbnail ||
                                  product?.images?.[0]?.urlThumbnail ||
                                  ""
                                )}
                                loading="lazy"
                                height="100"
                                width="100"
                                alt={decodeURIComponent(
                                  product?.defaultImage?.altText || ""
                                )}
                              />
                            </span>
                            <span className="col-6 d-md-none">
                              {product?.productUnit && (
                                <span className="ml-2 productblock__label d-inline-block">
                                  {product?.productUnit}{" "}
                                </span>
                              )}
                            </span>
                          </span>
                        </div>
                      </div>
                      <span className="mt-3 mb-2 productblock__specs d-block">
                        <ul>
                          {product?.advantages?.slice(0, 4).map(
                            (advantage) =>
                              advantage?.value?.stringValue && (
                                <li
                                  key={advantage?.value?.stringValue}
                                  data-icon-before=""
                                  className="pb-2 pl-4 pb-md-1"
                                >
                                  {advantage?.value?.stringValue}
                                </li>
                              )
                          )}
                        </ul>
                      </span>

                      {/* <div className="row no-gutters align-items-center"></div> */}
                      {!product?.variants || !product?.variants?.length ? (
                        <div
                          className="row no-gutters align-items-center"
                          style={{ marginRight: "25%" }}
                        >
                          {product?.unitsOfMeasure[0]?.availability
                            ?.canAddToCart === true ? (
                            <WvCategoryActionBar product={product} />
                          ) : null}
                        </div>
                      ) : null}

                      <span className="productblock__price special">
                        <div className="hexagon-product hexagon-primary">
                          <div className="hexagon-inner">
                            <div className="hexagon-content">
                              {product?.salePriceInclTax ? (
                                <div
                                  style={{
                                    top: `${`${product.priceInclTax.value}${product.priceInclTax.currencyCode}`
                                      ?.length * 3.2
                                      }px`,
                                  }}
                                  className="product-price-inner sale"
                                >
                                  <span
                                    className="price-old"
                                    data-price={product?.priceInclTax?.value}
                                  >
                                    {formatMoneyWithCurrency(
                                      localeFullName,
                                      product?.priceInclTax?.currencyCode
                                        ? product?.priceInclTax
                                        : fallbackMoney()
                                    )}
                                  </span>
                                  <span
                                    className="price"
                                    data-price={
                                      product?.salePriceInclTax?.value
                                    }
                                  >
                                    {formatMoneyWithCurrency(
                                      localeFullName,
                                      product?.salePriceInclTax?.currencyCode
                                        ? product?.salePriceInclTax
                                        : fallbackMoney()
                                    )}
                                  </span>
                                  {!taxExemptedCustomer(customer?.type) ? (
                                    <span className="mt-1 ex-btw">
                                      <span
                                        className="d-inline"
                                        data-price={
                                          product?.salePriceExclTax?.value
                                        }
                                      >
                                        {formatMoneyWithCurrency(
                                          localeFullName,
                                          product?.salePriceExclTax
                                            ?.currencyCode
                                            ? product?.salePriceExclTax
                                            : fallbackMoney()
                                        )}
                                      </span>{" "}
                                      {$pt("product_label_excluding_tax")}
                                    </span>
                                  ) : (
                                    <span className="mt-1 ex-btw">
                                      {$pt("product_label_tax_exempted_price")}
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <div
                                  className="product-price-inner"
                                  style={{
                                    top: `${`${product.priceInclTax.value}${product.priceInclTax.currencyCode}`
                                      ?.length * 3.2
                                      }px`,
                                  }}
                                >
                                  <span
                                    className="price"
                                    data-price={product?.priceInclTax?.value}
                                  >
                                    {formatMoneyWithCurrency(
                                      localeFullName,
                                      product.priceInclTax.currencyCode
                                        ? product.priceInclTax
                                        : fallbackMoney()
                                    )}
                                  </span>
                                  {!taxExemptedCustomer(customer?.type) ? (
                                    <span className="mt-1 ex-btw">
                                      <span
                                        className="d-inline"
                                        data-price={
                                          product?.priceExclTax?.value
                                        }
                                      >
                                        {formatMoneyWithCurrency(
                                          localeFullName,
                                          product?.priceExclTax?.currencyCode
                                            ? product?.priceExclTax
                                            : fallbackMoney()
                                        )}
                                      </span>{" "}
                                      {$pt("product_label_excluding_tax")}
                                    </span>
                                  ) : (
                                    <span className="mt-1 ex-btw">
                                      {$pt("product_label_tax_exempted_price")}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </span>
                    </WvLink>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div
            className={`component-products-next d-none d-lg-block top-unset js-scroller-next ${!canScrollRight ? "disabled" : ""
              }`}
            data-icon-before=""
            onClick={() => scroll("RIGHT")}
          ></div>
        </div>
      </div>
    </div>
  );
};
export default WvProductPopularAlternatives;
